import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getProcessRecord, labelProcessRecord, listRecordRuns, revokeProcessLabel, PrismArchive } from '../../api/assets';
import { formatTime } from '../../utils/time';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import { viewUrl } from '../../utils/imgUrl';
import { toast } from '../../utils/toast';
import { AdoptBadge, BeforeAfterImages, EffectBadge, LinkedMaterial, QUALITY_LABEL, SatisfactionBadge, defectText, isSentinelMaterialID, satisfyReasonText } from './parts';
import { QUALITY_INDICATORS, QUALITY_V1, QUALITY_V2, UNABLE_REASON_LABEL, isV2Quality } from './quality';
import { useProcessTypeText } from '../../utils/dictOptions';
import { useMModeText } from '../../utils/mmodeText';
import { useParamValueText } from '../../utils/paramValueText';
import { useCan } from '../../store';
import Pagination from '../../components/Pagination';

// 加工记录详情页 (/assets/process-records/:archiveId): 实时代理 prism —
// 加工前/后对比图 + 推荐 vs 实际参数对照 + AI 效果评级 + 人工复核 (写回 prism 源头) + 历次加工.

const PARAM_KEYS: [string, string][] = [
    ['craft', '工艺'], ['power', '功率'], ['speed', '速度'], ['repeat', '次数'],
    ['laser', '激光'], ['process_head', '加工头'], ['processing_mode', '加工模式'],
];


function ProcessDetail() {
    const canUpdate = useCan('data_asset', 'update');
    // 工艺类型名从 dict process_type 取 (跨框架合并), 不在页面里维护常量表.
    const craftText = useProcessTypeText();
    const modeText = useMModeText();
    // 参数值取名统一走公共取值器 (加工模式/工艺/加工头/位图模式… 各自的字典).
    const paramText = useParamValueText();

    const { id } = useParams<{ id: string }>();
    const [d, setD] = useState<PrismArchive | null>(null);
    const [error, setError] = useState('');
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);
    const [labeling, setLabeling] = useState(false);
    // 人工复核走 v2「加工质量评估」口径 (prism 0048 起): 四结论 + 命中的核心指标 / 无法判断原因码.
    // pick = 正在填的结论 (效果完美点一下即提交, 其余两步: 选结论 → 补指标/原因 → 提交).
    const [pick, setPick] = useState('');
    const [inds, setInds] = useState<string[]>([]);
    const [reason, setReason] = useState('');

    // 平台推荐参数 (官方材料回查): 参数库的键是 camelCase, 这张表的行键沿用 prism 的 snake_case.
    // 同机型挂多个 deviceKey 时每个一列并排看, 不合并也不猜是哪个 —— 差异可能是功率档
    // (M1 = blue 5W / blue 10W), 也可能是光源 (F1 Ultra = GS002-blue-20W / GS002-red-20W,
    // 瓦数相同只差光源), 所以文案按 deviceKey 说, 不写死成"功率档".
    const plat = d?.platform_recommended || null;
    const PLAT_KEY_MAP: Record<string, string> = {
        craft: 'processingType', power: 'power', speed: 'speed', repeat: 'repeat',
        process_head: 'processHead', processing_mode: 'processingMode',
    };
    const platValueOf = (params: Record<string, unknown> | null | undefined, key: string): unknown => {
        const k = PLAT_KEY_MAP[key];
        return params && k ? params[k] : undefined;
    };
    // device_key 去掉机型前缀当列头 (MLM-blue-5W → blue 5W), 全名放 title.
    const deviceKeyTail = (dk: string) => dk.split('-').slice(1).join(' ') || dk;
    const platRows = plat?.rows || [];

    const load = useCallback(() => {
        getProcessRecord(Number(id))
            .then(setD)
            .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    }, [id]);
    useEffect(() => { load(); }, [load]);

    // 再点一次已选中的档位 = 撤销 (误点可反悔); 撤销后展示退回 AI 判定.
    const onRevoke = async () => {
        if (!d || labeling) return;
        setLabeling(true);
        try {
            await revokeProcessLabel(d.archive_id);
            toast('已撤销人工复核, 恢复 AI 判定');
            load();
        } catch (e) {
            toast(e instanceof ApiError ? e.message : String(e));
        } finally {
            setLabeling(false);
        }
    };
    const submitLabel = async (quality: string, indicators: string[], unableReason: string) => {
        if (!d || labeling) return;
        setLabeling(true);
        try {
            await labelProcessRecord(d.archive_id, {
                quality,
                indicators: indicators.length > 0 ? indicators : undefined,
                unable_reason: unableReason || undefined,
            });
            toast(`已复核为「${QUALITY_LABEL[quality]}」(已写回 prism)`);
            setPick(''); setInds([]); setReason('');
            load();
        } catch (e) {
            toast(e instanceof ApiError ? e.message : String(e));
        } finally {
            setLabeling(false);
        }
    };
    // v1 四档 (源库还没升到 prism 0048 时的回退): 点一下即提交, 再点一次已生效的 = 撤销.
    const onPickV1 = async (q: string) => {
        if (!d || labeling) return;
        if (d.effect?.source === 'manual' && d.effect.quality === q) {
            await onRevoke();
            return;
        }
        setLabeling(true);
        try {
            await labelProcessRecord(d.archive_id, { quality: q });
            toast(`已复核为「${QUALITY_LABEL[q]}」(已写回 prism)`);
            load();
        } catch (e) {
            toast(e instanceof ApiError ? e.message : String(e));
        } finally {
            setLabeling(false);
        }
    };
    const onPick = (q: string) => {
        if (!d || labeling) return;
        // 再点一次已生效的人工结论 = 撤销 (误点可反悔); 撤销后展示退回 AI 判定.
        if (pick === '' && d.effect?.source === 'manual' && d.effect.quality === q) {
            void onRevoke();
            return;
        }
        if (q === 'perfect') {
            void submitLabel(q, [], '');
            return;
        }
        setPick(q); setInds([]); setReason('');
    };

    if (error) return <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>;
    if (!d) return <div className="text-center text-slate-400 text-sm py-16">加载中…</div>;

    const rec = (d.recommended || {}) as Record<string, unknown>;
    const act = (d.actual || {}) as Record<string, unknown>;
    const diff = d.diff || [];
    // 识别侧有没有给候选参数 —— 没有就不摆这一列, 免得一列全是 —
    const hasRecogRec = Object.keys(rec).length > 0;
    const effect = d.effect || {};
    // 指标词表按这次存档的工艺取; 范围外的工艺 (切割等) 没有指标可勾, 只记结论.
    const indicatorOptions = QUALITY_INDICATORS[String(act.craft || '')] || [];
    // 源库没升到 prism 0048 (缺 indicators/unable_reason 两列) 时后端会拒绝 v2 复核, 退回 v1 四档.
    const v2Ready = d.quality_v2_ready !== false;

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-xs text-slate-400 mb-1">
                        <Link to="/assets/process-records" className="text-brand-600 hover:underline">← 返回 材料加工</Link>
                    </div>
                    <div className="text-base font-semibold truncate">{d.material_name || (isSentinelMaterialID(d.material_id) ? '无材料标识' : `材料 ${d.material_id}`)}</div>
                    <div className="text-xs text-slate-500 mt-1">
                        {d.device_model} · {craftText(String(act.craft || ''))} ·
                        归档于 {formatTime(d.archived_at)} · 来源 {d.source || '—'} · 归档号 {d.archive_id}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <AdoptBadge verdict={d.verdict} wording="v44" />
                    <EffectBadge quality={effect.quality || ''} source={effect.source} wording="three" score={effect.score} />
                    <SatisfactionBadge value={d.satisfaction} />
                </div>
            </div>

            <div className="grid grid-cols-[320px_1fr] gap-5 items-start">
                {/* 左: 加工前/后对比图 (两张图已占满这一栏, 效果评级挪到了右栏溯源下面) */}
                <div className="space-y-3">
                    <div className="card p-3">
                        <BeforeAfterImages before={d.pre_image_url} after={d.image_url}
                            size={280} onZoom={setZoomSrc} />
                    </div>
                </div>

                {/* 右: 实际 vs 推荐参数对照 (实际值在前, 推荐在后; 参数库多功率档每档一列) + 溯源 + 效果评级/人工复核 */}
                <div className="space-y-4">
                    <div className="card p-4">
                        <div className="text-xs font-semibold text-slate-500 mb-2">
                            实际 vs 推荐参数
                            {diff.length > 0 && <span className="text-amber-500 font-normal"> · 用户调整了 {diff.join(' / ')}</span>}
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-slate-400 border-b border-slate-100">
                                        <th className="py-1.5 pr-4 font-medium whitespace-nowrap">参数</th>
                                        <th className="py-1.5 pr-4 font-medium whitespace-nowrap">实际值</th>
                                        {hasRecogRec && <th className="py-1.5 pr-4 font-medium whitespace-nowrap">识别推荐</th>}
                                        {platRows.map((r) => (
                                            <th key={r.deviceKey} className="py-1.5 pr-4 font-medium whitespace-nowrap" title={`参数库 ${r.deviceKey}`}>
                                                <span className="text-teal-600">参数库</span>
                                                {platRows.length > 1 && (
                                                    <span className="ml-1 font-mono text-[10px] text-slate-400">{deviceKeyTail(r.deviceKey)}</span>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {PARAM_KEYS.map(([key, label]) => {
                                        const av = act[key];
                                        const rv = rec[key];
                                        const pvs = platRows.map((r) => platValueOf(r.params, key));
                                        if (av === undefined && rv === undefined && pvs.every((v) => v === undefined)) return null;
                                        const changed = diff.includes(key);
                                        const fmt = (v: unknown) => paramText(key, v);
                                        return (
                                            <tr key={key} className="border-b border-slate-50">
                                                <td className="py-1.5 pr-4 text-slate-500 whitespace-nowrap">{label}</td>
                                                <td className={`py-1.5 pr-4 font-medium whitespace-nowrap ${changed ? 'text-amber-600' : 'text-slate-700'}`}>
                                                    {fmt(av)}{changed ? ' ✎' : ''}
                                                </td>
                                                {hasRecogRec && <td className="py-1.5 pr-4 text-slate-600 whitespace-nowrap">{fmt(rv)}</td>}
                                                {pvs.map((v, i) => (
                                                    <td key={platRows[i].deviceKey} className="py-1.5 pr-4 text-slate-600 whitespace-nowrap">{fmt(v)}</td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {plat && (
                            <div className="mt-2 text-[11px] text-slate-400 space-y-1">
                                <div>
                                    「参数库」列 = 本平台官方参数库同条件推荐值：{plat.deviceName} · {craftText(plat.subProcess)}
                                    {plat.modeMatched ? '' : ` · ⚠ 该材料下没有「${modeText(plat.modeValue)}」的参数行，取自其它加工模式，仅供参考`}
                                </div>
                                {plat.ambiguous && (
                                    <div>
                                        该机型下有 {plat.rows.length} 个设备档位（{plat.rows.map((r) => r.deviceKey).join(' / ')}），
                                        存档只记到机型、不记光源与功率，分不出是哪一档，因此每档单列一栏，不替你选。
                                    </div>
                                )}
                                {!hasRecogRec && <div>识别未给出候选参数，所以没有「识别推荐」列。</div>}
                                <div>参数库列只作参照，不参与上方「用户调整了」的判定（那是识别当时推荐 vs 实际的比对）。</div>
                            </div>
                        )}
                    </div>

                    <div className="card p-4 text-xs text-slate-500 space-y-1.5">
                        <div className="text-xs font-semibold text-slate-500 mb-1">溯源</div>
                        <div>
                            源材料 ID: <span className="font-mono">{d.material_id || '—'}</span>
                            {d.material_id && (
                                <Link to={`/assets/process-records?tab=table`} className="ml-2 text-brand-600 hover:underline">
                                    回加工明细 →
                                </Link>
                            )}
                        </div>
                        <div className="flex items-start gap-2">
                            <span className="shrink-0">平台材料:</span>
                            <LinkedMaterial material={d.material} source={d.material_source} />
                        </div>
                        <div>
                            识别任务: {d.recognition?.task_id ? (
                                <Link to={`/assets/recognitions/${encodeURIComponent(d.recognition.task_id)}`}
                                    className="font-mono text-brand-600 hover:underline">{d.recognition.task_id}</Link>
                            ) : '无 (非识别链路)'}
                            {d.recognition?.candidate_rank && ` · 采用候选 #${d.recognition.candidate_rank}`}
                            {d.recognition?.recognized_at ? ` · 识别于 ${formatTime(d.recognition.recognized_at)}` : ''}
                        </div>
                    </div>

                    {/* 用户满意度: 用户自己在 Studio 里的评价, 与上面 AI/人工的效果评级是两件事 */}
                    <div className="card p-3 space-y-2">
                        <div className="text-xs font-semibold text-slate-500">用户满意度</div>
                        {d.satisfaction ? (
                            <div className="text-xs text-slate-600 space-y-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="text-slate-400 w-16 shrink-0 whitespace-nowrap">是否满意</span>
                                    <SatisfactionBadge value={d.satisfaction} />
                                </div>
                                <div className="flex items-start gap-2">
                                    <span className="text-slate-400 w-16 shrink-0 whitespace-nowrap">不满意原因</span>
                                    {d.satisfaction.reasons.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {d.satisfaction.reasons.map((c) => (
                                                <span key={c} className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-500 text-[11px]" title={c}>{satisfyReasonText(c)}</span>
                                            ))}
                                        </div>
                                    ) : <span className="text-slate-300">—</span>}
                                </div>
                                <div className="flex items-start gap-2">
                                    <span className="text-slate-400 w-16 shrink-0 whitespace-nowrap">备注</span>
                                    {d.satisfaction.note
                                        ? <span className="whitespace-pre-wrap break-words min-w-0">{d.satisfaction.note}</span>
                                        : <span className="text-slate-300">—</span>}
                                </div>
                                <div className="text-[11px] text-slate-400">评价于 {formatTime(d.satisfaction.rated_at)}</div>
                            </div>
                        ) : (
                            <div className="text-xs text-slate-400">
                                用户没有评价这次加工。满意度是 Studio 1.9 起加工后让用户选填的，之前的版本不上报，之后也只有少数加工有评价。
                            </div>
                        )}
                    </div>

                    <div className="card p-3 space-y-2">
                        <div className="text-xs font-semibold text-slate-500">效果评级</div>
                        {effect.quality ? (
                            <div className="text-xs text-slate-600 space-y-1.5">
                                <div>
                                    <EffectBadge quality={effect.quality} source={effect.source} wording="three" />
                                    {/* 百分制只有 v1 有; v2 (加工质量评估) 是定性结论 + 命中的指标, 没有分 */}
                                    {!isV2Quality(effect.quality) && <span className="ml-2">{effect.score ?? '—'} 分</span>}
                                    {effect.confidence != null && (
                                        <span className="ml-2 text-slate-400" title="表达的是图像可判读性, 不是判得有多准 —— 别当质量闸门">
                                            置信 {Math.round((effect.confidence || 0) * 100)}%
                                        </span>
                                    )}
                                </div>
                                {(effect.defects?.length ?? 0) > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                        {effect.defects?.map((t) => (
                                            <span key={t} className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-500 text-[11px]">{defectText(t)}</span>
                                        ))}
                                    </div>
                                )}
                                {(effect.indicators?.length ?? 0) > 0 && (
                                    <div className="flex flex-wrap items-center gap-1" title="加工质量评估判出不达标的核心指标">
                                        <span className="text-[11px] text-slate-400">不达标指标:</span>
                                        {effect.indicators?.map((t) => (
                                            <span key={t} className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px]">{t}</span>
                                        ))}
                                    </div>
                                )}
                                {effect.quality === 'unjudgable' && (
                                    <div className="text-slate-500">
                                        原因: {UNABLE_REASON_LABEL[effect.unable_reason || ''] || effect.unable_reason || '未给出'}
                                    </div>
                                )}
                                {effect.description && <div className="text-slate-500">{effect.description}</div>}
                                <div className="text-[11px] text-slate-400">
                                    {effect.source === 'ai' ? `AI (${effect.model || ''})` : '人工'} · {formatTime(effect.labeled_at)}
                                </div>
                            </div>
                        ) : (
                            <div className="text-xs text-slate-400">
                                还没打标。AI 判定是后台定时批任务，新存档会滞后出现 —— 不代表「没有效果」。
                            </div>
                        )}
                        <div className="pt-1 border-t border-slate-50 space-y-1.5">
                            <div className="flex items-center text-[11px] text-slate-400">
                                <span>人工复核 ({v2Ready ? '加工质量评估口径' : '旧四档口径'}, 写回 prism 源头)</span>
                                {effect?.source === 'manual' && (
                                    <button type="button" disabled={labeling} onClick={onRevoke}
                                        className="ml-auto text-rose-500 hover:underline disabled:opacity-40" hidden={!canUpdate}>撤销复核</button>
                                )}
                            </div>
                            {!v2Ready && (
                                <div className="text-[11px] text-amber-600">
                                    源库还没升级到 prism 迁移 0048，暂按旧四档复核；升级后自动切换为四结论 + 指标。
                                </div>
                            )}
                            <div className="flex gap-1.5">
                                {(v2Ready ? QUALITY_V2 : QUALITY_V1).map((q) => {
                                    const current = effect?.quality === q && effect.source === 'manual';
                                    const active = pick === q || (pick === '' && current);
                                    return (
                                        <button key={q} type="button" disabled={labeling}
                                            title={current && pick === '' ? '再点一次撤销此复核' : undefined}
                                            className={`px-2 py-1 rounded text-[11px] border ${active
                                                ? 'border-brand-500 bg-brand-50 text-brand-600'
                                                : 'border-slate-200 text-slate-500 hover:border-brand-300'}`}
                                            onClick={() => (v2Ready ? onPick(q) : onPickV1(q))}>
                                            {QUALITY_LABEL[q]}
                                        </button>
                                    );
                                })}
                            </div>
                            {(pick === 'minor_flaw' || pick === 'abnormal') && (
                                <div className="space-y-1.5">
                                    {indicatorOptions.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {indicatorOptions.map((name) => {
                                                const on = inds.includes(name);
                                                return (
                                                    <button key={name} type="button"
                                                        className={`px-1.5 py-0.5 rounded border text-[11px] ${on
                                                            ? 'border-amber-400 bg-amber-50 text-amber-700'
                                                            : 'border-slate-200 text-slate-500 hover:border-amber-300'}`}
                                                        onClick={() => setInds(on ? inds.filter((x) => x !== name) : [...inds, name])}>
                                                        {name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-[11px] text-slate-400">
                                            该工艺不在加工质量评估的指标范围内（仅矢量线条 / 矢量填充 / 位图雕刻有指标词表），只记结论。
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <button type="button" className="btn-primary text-[11px] px-2 py-1" disabled={labeling}
                                            onClick={() => submitLabel(pick, inds, '')}>
                                            提交{inds.length > 0 ? `（${inds.length} 项指标）` : ''}
                                        </button>
                                        <button type="button" className="text-[11px] text-slate-400 hover:underline" onClick={() => setPick('')}>取消</button>
                                        {pick === 'abnormal' && <span className="text-[11px] text-slate-400">「异常」会作为调参负样本；「轻微瑕疵」不会。</span>}
                                    </div>
                                </div>
                            )}
                            {pick === 'unjudgable' && (
                                <div className="flex items-center gap-2">
                                    <select className="sel text-[11px]" value={reason} onChange={(e) => setReason(e.target.value)}>
                                        <option value="">选择无法判断的原因…</option>
                                        {Object.entries(UNABLE_REASON_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                    </select>
                                    <button type="button" className="btn-primary text-[11px] px-2 py-1" disabled={labeling || !reason}
                                        onClick={() => submitLabel(pick, [], reason)}>提交</button>
                                    <button type="button" className="text-[11px] text-slate-400 hover:underline" onClick={() => setPick('')}>取消</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <RecordRuns archiveId={d.archive_id} craftText={craftText} />

            <ImageLightbox src={viewUrl(zoomSrc)} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

const RUNS_PAGE_SIZE = 20;

/**
 * 历次加工 —— 这条存档所属「材料 × 用户」的每一次上报, 带各自的参数与效果.
 *
 * 这是看调参轨迹的地方: 同一个人对同一个材料连着试几次, 功率/速度怎么变、效果跟着怎么变,
 * 一张表就看出来了 (实测有用户 45→50→45→60 一路试, 效果从 best 掉到 poor).
 *
 * 必须分页: 线上最极端的「材料 × 用户」有 1,085 次 (平均只有 5.8 次, 是长尾).
 */
function RecordRuns({ archiveId, craftText }: {
    archiveId: number;
    craftText: (code: string) => string;
}) {
    const [scope, setScope] = useState<'user' | 'material'>('user');
    const [page, setPage] = useState(1);
    const [runs, setRuns] = useState<PrismArchive[]>([]);
    const [total, setTotal] = useState(0);
    const [userID, setUserID] = useState('');
    // 后端回的材料标识: 哨兵行 (没带材料标识) 回空串, 空态要按这个分支给准话.
    const [materialID, setMaterialID] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => { setPage(1); }, [archiveId, scope]);
    useEffect(() => {
        let dropped = false;
        setLoading(true);
        listRecordRuns(archiveId, { scope, page, pageSize: RUNS_PAGE_SIZE })
            .then((res) => {
                if (dropped) return;
                setRuns(res.data || []);
                setTotal(res.total);
                setUserID(res.user_id || '');
                setMaterialID(res.material_id || '');
            })
            .catch(() => { if (!dropped) { setRuns([]); setTotal(0); setMaterialID(null); } })
            .finally(() => { if (!dropped) setLoading(false); });
        // 切 scope / 翻页时旧请求可能后到, 用标志位丢弃过期响应.
        return () => { dropped = true; };
    }, [archiveId, scope, page]);

    const totalPages = Math.max(1, Math.ceil(total / RUNS_PAGE_SIZE));
    const num = (v: unknown) => (v === undefined || v === null ? '—' : String(v));

    return (
        <div className="card p-4 mt-3">
            <div className="flex items-center gap-3 mb-2">
                <div className="text-sm font-semibold text-slate-700">历次加工</div>
                <div className="flex rounded-md overflow-hidden border border-slate-200 text-[11px]">
                    {([['user', '这个用户'], ['material', '全部用户']] as const).map(([v, label]) => (
                        <button key={v} type="button"
                            className={`px-2.5 py-1 ${scope === v ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                            onClick={() => setScope(v)}>{label}</button>
                    ))}
                </div>
                <div className="ml-auto text-xs text-slate-400">
                    共 {total.toLocaleString()} 次
                    {scope === 'user' && userID && <span className="ml-1 font-mono">· UID {userID}</span>}
                </div>
            </div>

            {loading && runs.length === 0 ? (
                <div className="text-center text-slate-400 py-6 text-sm">加载中…</div>
            ) : runs.length === 0 ? (
                <div className="text-center text-slate-400 py-6 text-sm">
                    {materialID === ''
                        ? '这条上报没带材料标识，归不到任何材料上，因此没有历次加工可看。'
                        : scope === 'user' && !userID
                            ? '这条上报没带用户标识，匿名上报之间分不出是不是同一个人，无法按用户归堆。'
                            : '没有可归堆的历次加工。'}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="text-left text-[10px] font-extrabold uppercase text-slate-400 border-b border-slate-100"
                                style={{ letterSpacing: '1.5px' }}>
                                <th className="py-2 pr-3 whitespace-nowrap">加工时间</th>
                                <th className="py-2 pr-3 whitespace-nowrap">设备</th>
                                <th className="py-2 pr-3 whitespace-nowrap">工艺</th>
                                <th className="py-2 pr-3 text-right whitespace-nowrap">功率</th>
                                <th className="py-2 pr-3 text-right whitespace-nowrap">速度</th>
                                <th className="py-2 pr-3 text-right whitespace-nowrap">次数</th>
                                <th className="py-2 pr-3 whitespace-nowrap">参数采纳</th>
                                <th className="py-2 pr-3 whitespace-nowrap">加工质量</th>
                                {scope === 'material' && <th className="py-2 pr-3 whitespace-nowrap">用户</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map((r) => {
                                const a = (r.actual || {}) as Record<string, unknown>;
                                const here = r.archive_id === archiveId;
                                return (
                                    <tr key={r.archive_id}
                                        className={`border-b border-slate-50 ${here ? 'bg-amber-50' : ''}`}>
                                        <td className="py-2 pr-3 font-mono whitespace-nowrap">
                                            {here ? (
                                                <span title="当前这一条">▶ {formatTime(r.archived_at)}</span>
                                            ) : (
                                                <Link to={`/assets/process-records/${r.archive_id}`}
                                                    className="text-brand-600 hover:underline">{formatTime(r.archived_at)}</Link>
                                            )}
                                        </td>
                                        <td className="py-2 pr-3 font-mono text-slate-600 whitespace-nowrap">{r.device_model || '—'}</td>
                                        <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                                            {a.craft ? craftText(String(a.craft)) : '—'}
                                        </td>
                                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(a.power)}</td>
                                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(a.speed)}</td>
                                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(a.repeat)}</td>
                                        <td className="py-2 pr-3"><AdoptBadge verdict={r.verdict} wording="v44" /></td>
                                        <td className="py-2 pr-3"><EffectBadge quality={r.effect?.quality || ''} wording="three" /></td>
                                        {scope === 'material' && (
                                            <td className="py-2 pr-3 text-[11px] text-slate-500 truncate max-w-[160px]">
                                                {r.user_email || r.user_id || '匿名'}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {totalPages > 1 && (
                <div className="flex items-center justify-end pt-3 text-xs text-slate-500">
                    <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </div>
            )}

            <div className="pt-2 text-[11px] text-slate-400">
                按<b>加工时间倒序</b>；高亮那行是当前这一条，点别的时间可以跳过去。
                「功率/速度/次数」取存档里第一个加工元素的实际值，与上方「实际 vs 推荐参数」同一口径。
            </div>
        </div>
    );
}

export default ProcessDetail;
